import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Team, Event } from '../utils/types';
import { apiGet, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const TeamsList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [teams, setTeams] = useState<Team[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [eventFilter, setEventFilter] = useState<string>(searchParams.get('eventId') || 'all');
  const [memberFilter, setMemberFilter] = useState<string>(searchParams.get('filter') || 'all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { token, user } = useAuth();

  useEffect(() => {
    loadTeamsAndEvents();
  }, [token]);

  const loadTeamsAndEvents = async () => {
    if (!token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const [teamsData, eventsData] = await Promise.all([
        apiGet<Team[]>('/teams', token),
        apiGet<Event[]>('/events', token)
      ]);

      if (!searchParams.get('eventId') && eventFilter === 'all') {
        const currentActiveEvent = eventsData.find(event => event.state === 'open');
        if (currentActiveEvent) {
          setEventFilter(currentActiveEvent.event_id);
        }
      }

      setTeams(teamsData);
      setEvents(eventsData);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load teams');
      }
    } finally {
      setLoading(false);
    }
  };

  // Find the current active event (state: 'open')
  const activeEvent = events.find(e => e.state === 'open');

  // Only show teams for the active event for participants
  const isParticipant = !user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER');

  const filteredTeams = teams.filter(team => {
    let matches = true;
    
    // Event filtering
    if (isParticipant && activeEvent) {
      matches = matches && team.event_id === activeEvent.event_id;
    } else if (eventFilter !== 'all') {
      matches = matches && team.event_id === eventFilter;
    }
    
    // Member availability filtering
    if (memberFilter === 'looking') {
      matches = matches && team.looking_for_members;
    } else if (memberFilter === 'full') {
      matches = matches && !team.looking_for_members;
    }
    
    // Search query filtering
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const teamName = team.name.toLowerCase();
      const teamDescription = (team.description || '').toLowerCase();
      const teamSkills = team.desired_skills.join(' ').toLowerCase();
      
      matches = matches && (
        teamName.includes(query) ||
        teamDescription.includes(query) ||
        teamSkills.includes(query)
      );
    }
    
    return matches;
  });

  const getEventName = (eventId: string) => {
    const event = events.find(e => e.event_id === eventId);
    return event ? event.name : 'Unknown Event';
  };

  const getEventCapacity = (eventId: string) => {
    const event = events.find(e => e.event_id === eventId);
    return event?.max_team_size ?? 5;
  };

  if (loading) {
    return <div className="loading">Loading teams...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2>👥 Teams</h2>
        <Link to="/create-team" className="btn btn-success">
          🏗️ Create New Team
        </Link>
      </div>

      {error && (
        <div className="error">
          {error}
          <button 
            onClick={loadTeamsAndEvents} 
            className="btn btn-secondary" 
            style={{ marginLeft: '10px' }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '15px', 
        marginBottom: '20px' 
      }}>
        {/* Search input */}
        <div>
          <label htmlFor="search-teams" style={{ display: 'block', marginBottom: '5px' }}>
            🔍 Search teams:
          </label>
          <input
            id="search-teams"
            type="text"
            placeholder="Search by name, description, or skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '8px', 
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
        
        {/* Only show event filter for organizers or if not in active event view */}
        {!isParticipant && (
          <div>
            <label htmlFor="event-filter" style={{ display: 'block', marginBottom: '5px' }}>
              Filter by event:
            </label>
            <select 
              id="event-filter"
              value={eventFilter} 
              onChange={(e) => setEventFilter(e.target.value)}
              style={{ width: '100%', padding: '5px' }}
            >
              <option value="all">All Events</option>
              {events.map(event => (
                <option key={event.event_id} value={event.event_id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="member-filter" style={{ display: 'block', marginBottom: '5px' }}>
            Filter by availability:
          </label>
          <select 
            id="member-filter"
            value={memberFilter} 
            onChange={(e) => setMemberFilter(e.target.value)}
            style={{ width: '100%', padding: '5px' }}
          >
            <option value="all">All Teams</option>
            <option value="looking">Looking for Members</option>
            <option value="full">Not Looking for Members</option>
          </select>
        </div>
      </div>

      {filteredTeams.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h3>No teams found</h3>
          <p>
            {(() => {
              if (teams.length === 0) {
                return "There are no teams yet. Create your first team to get started!";
              }
              if (searchQuery.trim()) {
                return `No teams match your search "${searchQuery}". Try adjusting your search terms.`;
              }
              return "No teams match your current filters.";
            })()}
          </p>
          {(() => {
            if (teams.length === 0) {
              return (
                <Link to="/create-team" className="btn" style={{ marginTop: '15px' }}>
                  Create First Team
                </Link>
              );
            }
            if (searchQuery.trim()) {
              return (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="btn btn-secondary" 
                  style={{ marginTop: '15px' }}
                >
                  Clear Search
                </button>
              );
            }
            return null;
          })()}
        </div>
      ) : (
        <div className="grid">
          {filteredTeams.map((team) => (
            <div key={team.team_id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <h3 style={{ margin: 0, flex: 1, marginRight: '10px' }}>
                  <Link 
                    to={`/teams/${team.team_id}`}
                    style={{ 
                      textDecoration: 'none', 
                      color: 'inherit',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={team.name} // Show full name on hover
                  >
                    {team.name.length > 20 ? `${team.name.substring(0, 20)}...` : team.name}
                  </Link>
                </h3>
                {team.looking_for_members && (
                  <span className="badge badge-open">
                    👋 Looking
                  </span>
                )}
              </div>
              
              <p style={{ color: '#666', marginBottom: '10px', fontSize: '12px' }}>
                📅 {getEventName(team.event_id)}
              </p>

              <p style={{ color: '#444', marginBottom: '10px', fontSize: '13px', fontWeight: 600 }}>
                👥 {team.member_count ?? 0}/{getEventCapacity(team.event_id)} members
              </p>
              
              <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
                {team.description && team.description.length > 100 
                  ? `${team.description.substring(0, 100)}...` 
                  : team.description || 'No description provided'
                }
              </p>
              
              {team.desired_skills.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                    Looking for:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {team.desired_skills.slice(0, 3).map((skill) => (
                      <span 
                        key={skill}
                        style={{
                          background: '#e9ecef',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          color: '#495057'
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                    {team.desired_skills.length > 3 && (
                      <span style={{ fontSize: '11px', color: '#666' }}>
                        +{team.desired_skills.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '15px',
                paddingTop: '15px',
                borderTop: '1px solid #eee'
              }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  👤 {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                </div>
                <Link 
                  to={`/teams/${team.team_id}`}
                  className="btn"
                  style={{ fontSize: '12px', padding: '5px 10px' }}
                >
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamsList;
